import { tagName } from "@ember-decorators/component"
import { getOwner } from "@ember/application"
import Component from "@ember/component"
import { action } from "@ember/object"
import { inject as service } from "@ember/service"
import type { CardStack } from "@shared/models/card-stack"
import type { Course } from "@shared/models/course"
import type { Planbook } from "@shared/models/planbook"
import type { PlanbookCalendarDateCombined } from "@shared/models/planbook-calendar-date-combined"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import { handleError } from "cc-frontend/error-handling"
import copyAllLessonsForDayAction from "cc-frontend/lib/actions/planbook/lessons/PLANBOOK_COPY_ALL_LESSONS_FOR_DAY"
import PLANBOOK_DATE_RESET_TO_TEMPLATE from "cc-frontend/lib/actions/planbook/PLANBOOK_DATE_RESET_TO_TEMPLATE"
import { findCourseDateCustom } from "cc-frontend/lib/find-course-date"
import { getCardStackIdForDate } from "cc-frontend/lib/unit-utils"
import type Router from "cc-frontend/router"
import type CourseCalendarEditor from "cc-frontend/services/course-calendar-editor"
import type Dialog from "cc-frontend/services/dialog"
import type Dispatcher from "cc-frontend/services/dispatcher"
import type EmberStore from "cc-frontend/services/ember-store"
import type LessonEditor from "cc-frontend/services/lesson-editor"
import type ModalManager from "cc-frontend/services/modal-manager"
import type RotationEditor from "cc-frontend/services/rotation-editor"
import type Session from "cc-frontend/services/session"
import _, { isNil } from "lodash"
import moment from "moment"
import styles from "./body.module.css"

@tagName("")
export default class DayMenuPrimaryMenuComponent extends Component {
  readonly styles = styles
  // Args
  planbook!: Planbook
  planbookDateCombined!: PlanbookCalendarDateCombined
  courses!: Array<Course>
  rotationCalendar!: RotationCalendar
  routine!: CardStack
  closeMenu!: Function
  isOff!: boolean
  dateString!: string
  isFilteringClasses!: boolean
  unitId!: string
  hiddenCourseIds: Array<string> = []
  goToMenu!: Function

  // Services
  @service notifier
  @service("ember-store") store!: EmberStore
  @service dispatcher!: Dispatcher
  @service rotationEditor!: RotationEditor
  @service courseCalendarEditor!: CourseCalendarEditor
  @service lessonEditor!: LessonEditor
  @service dialog!: Dialog
  @service session!: Session
  @service modalManager!: ModalManager

  get hasRotationReset() {
    let dateAnnotation = this.planbookDateCombined.rotationCalendarException
    return (
      dateAnnotation &&
      dateAnnotation.type === "date-annotation-rotation-calendar-single" &&
      dateAnnotation.attributes.rotationShould === "CHANGE_AND_CYCLE_SHOULD_RESET"
    )
  }

  // actions
  @action
  bumpLessonsForwardDay() {
    this.closeMenu()
    this.courseCalendarEditor.bumpDayForward(
      this.dateString,
      this.courses,
      this.planbook,
      this.rotationCalendar,
    )
  }

  @action
  pullLessonsBackwardsToDay() {
    let coursesWithOneDayUnits = _.filter(this.courses, (course) => {
      return this.courseCalendarEditor.doesCourseDateHaveTinyUnit(course, this.dateString)
    })

    if (coursesWithOneDayUnits.length > 0) {
      window
        .swal({
          title:
            "To pull lessons backwards, you need to first delete the unit that starts and ends on this date",
          text: "Head over to the unit planner to delete the unit or make it longer.",
          type: "warning",
          showCancelButton: false,
        })
        .catch((error) => {
          if (error instanceof Error) handleError(error)
        })
    } else {
      this._pullDayBackwards()
    }
    this.closeMenu()
  }

  @action
  copyLessonsForDay() {
    this.closeMenu()

    if (this.session.isAuthenticated !== true || this.session.userId === null) {
      this.session.mustSignInToProceed("Copy Lessons for Day")
      return
    }

    let lessonsForCourses = _.compact(
      _.map(this.courses, (course) => {
        let cardStackId = getCardStackIdForDate(course.attributes.calendar.dates, this.dateString)
        let cardStack = this.store.findInMemory("card-stack", cardStackId)
        if (cardStack) {
          let attrs = _.cloneDeep(cardStack.attributes)
          attrs.parentCardStack = {
            id: cardStack.id,
            version: cardStack.attributes.version,
            planbookId: this.planbook.id,
            courseId: course.id,
            ownerIds: [this.planbook.relationships.owner.data.id],
            source: "paste",
          }
          if (attrs.cards) {
            for (const card of attrs.cards) {
              card.attributes["commentIds"] = []
            }
          }
          return {
            courseId: course.id,
            cardStackAttrs: attrs,
          }
        } else {
          return undefined
        }
      }),
    )
    this.dispatcher.dispatchPreparedAction(
      copyAllLessonsForDayAction({
        currentUserId: this.session.userId,
        lessonsForCourses: lessonsForCourses,
        dateString: this.dateString,
        ownerIds: [this.planbook.relationships.owner.data.id],
        planbookId: this.planbook.id,
      }),
    )

    this.notifier.success("", {
      title: `Day Copied!`,
      message: "Click a day's paste button to paste.",
      duration: 2000,
    })
  }

  @action
  pasteLessonsForDay() {
    if (!isNil(this.session.userId)) {
      let currentUser = this.store.find("user", this.session.userId!).then((user) => {
        if (isNil(user?.attributes?.clipboard?.day?.lessonsForCourses)) return

        let lessonsForCourses = _.cloneDeep(user.attributes.clipboard.day.lessonsForCourses)
        this.lessonEditor.pasteAllLessonsForDay(
          this.dateString,
          this.planbook,
          this.courses,
          this.rotationCalendar,
          lessonsForCourses,
        )
      })
    }
    this.closeMenu()
  }

  @action
  makeFullScreen() {
    this.closeMenu()
    ;(getOwner(this)!.lookup("router:main") as Router).transitionTo("planbookv5.planner.day", {
      queryParams: { date: this.dateString },
    })
  }

  @action
  printOrDownload() {
    this.closeMenu()

    if (this.session.isAuthenticated !== true) {
      this.session.mustSignInToProceed("Print/Download Day")
      return
    }

    if (_.includes(this.session.user.content.meta.permissions, "can-print-day")) {
      this.modalManager.openModal("planner/modals/print-download", {
        courseIds: _.chain(this.courses)
          .map((course) => course.id)
          .reject((courseId) => _.includes(this.hiddenCourseIds, courseId))
          .value(),
        planbookId: this.planbook.id,
        startDate: this.dateString,
        endDate: this.dateString,
        layout: "planbook-date:single",
        currentUserId: this.session.userId,
        isFilteringClasses: this.isFilteringClasses,
        canPrint: _.includes(this.session.user.content.meta.permissions, "can-print-day"),
        dateRangeString: moment(this.dateString).format("dddd, MMMM Do YYYY"),
        unitId: this.unitId,
      })
    } else {
      this.modalManager.openModal("planner/modals/print-paywall", {})
    }
  }

  @action
  eraseAllLessonsForDay() {
    this.closeMenu()
    this.dialog
      .blank("dialogs/confirm-delete-all-lessons-on-day", {
        // When the day isn't off, we delete histories by default so that the user
        // can bump lessons into these slots.
        shouldRemoveHistory: !this.planbookDateCombined.isOff,
        className: "dialog--create-annotation",
        isOff: this.planbookDateCombined.isOff,
      })
      .then((shouldRemoveHistory: boolean) => {
        if (shouldRemoveHistory) {
          let coursesWithCardStacks = _.filter(this.courses, (course) => {
            let courseDate = _.find(course.attributes.calendar.dates, (date) => {
              return (
                date.type === "course-date-custom" && date.attributes.dateString === this.dateString
              )
            })
            return courseDate !== undefined && courseDate.attributes.cardStackId !== null
          })
          this.courseCalendarEditor.eraseAllLessonsForDay(
            coursesWithCardStacks,
            this.dateString,
            this.planbook.id,
          )
        } else {
          let cardStackIdForCourse = _.compact(
            _.map(this.courses, (course) => {
              let cd = findCourseDateCustom(course, this.dateString)
              if (cd) {
                return {
                  courseId: course.id,
                  cardStackId: cd.attributes.cardStackId,
                }
              } else {
                return undefined
              }
            }),
          )
          let lessons = _.compact(
            _.map(cardStackIdForCourse, ({ cardStackId }) => {
              return this.store.findInMemory("card-stack", cardStackId)
            }),
          )
          this.lessonEditor.clearAllLessonsForDay(lessons, this.dateString, this.planbook.id)
        }
      })
      .catch((error) => {
        if (error instanceof Error) handleError(error)
      })
  }

  @action resetToTemplate() {
    this.closeMenu()
    swal({
      title: "Change to normal schedule?",
      text: "This will erase any notecards you've added to the day outside of lessons",
      type: "warning",
      showCancelButton: true,
    })
      .then(() => {
        this.dispatcher.dispatchAction(PLANBOOK_DATE_RESET_TO_TEMPLATE, {
          planbookId: this.planbook.id,
          currentCardStackId: this.planbookDateCombined.cardStackId,
          dateString: this.dateString,
          dateId: this.planbookDateCombined.planbookDateId,
          rotationDayTitle: this.planbookDateCombined.rotationDayTitle,
        })
        let formattedDate = dateFns.format(dateFns.parse(this.dateString), "MMM, DD, YYYY")

        this.notifier.success("", {
          title: `Great! ${formattedDate} is now using the normal schedule.`,
          message: "Click the Undo Button to undo this.",
          duration: 5000,
        })
      })
      .catch((error) => {
        if (error instanceof Error) handleError(error)
      })
  }

  _pullDayBackwards() {
    // this finds courses with lessons/ not blank lessons
    // let courseDate = this.lessonEditor.pullLessonBackwardsCheckIfLesson(course, this.dateString)

    let coursesWithLessons = _.filter(this.courses, (course) => {
      return this.courseCalendarEditor.doesCourseDateHaveLesson(course, this.dateString)
    })
    if (coursesWithLessons.length > 0) {
      let courseTitles = _.map(coursesWithLessons, (course) => course.attributes.title).join(", ")
      this.dialog
        .blank("dialogs/confirm-pull-backwards", {
          shouldRemoveHistory: false,
          className: "dialog--create-annotation",
          courseTitles: courseTitles,
        })
        .then(() => {
          this.courseCalendarEditor.pullMultipleLessonsBackwards(
            this.dateString,
            this.courses,
            this.planbook,
            this.rotationCalendar,
          )
        })
        .catch((error) => {
          if (error instanceof Error) handleError(error)
        })
    } else {
      this.courseCalendarEditor.pullMultipleLessonsBackwards(
        this.dateString,
        this.courses,
        this.planbook,
        this.rotationCalendar,
      )
    }
  }
}
